import ResizableComponent from "../../Layouts/ResizableComponent";
import HeaderModes from "../../Layouts/Navbar/HeaderModes";
import Navbar from "../../Layouts/Navbar/Navbar";
import React, { useEffect, useState } from "react";
import axiosInstance, { handleApiError } from "../../services/axiosService";
import { getTreeDataForConnectors } from "../../utils/treeData/treeDataUtils";
import { useTreeContext } from "../../context/TreeDataContext";
import LeftSideBar from "../../components/SideBar/LeftSideBar";
import Button from "../../components/FormComponents/Button";
import { Outlet, useNavigate } from "react-router-dom";
import Loader from "../../Layouts/Loader/Loader";
import { deleteFolderOfFile, editFolderOrFile, insertNewFileOrFolder } from "../../utils/treeData/folderEditorUtils";
import fileFolderConfig from "../../commonFunctions/fileFolderConfig";
import { toastSuccess } from "../../utils/toastUtils";
import { useConnectorsContext } from "../../context/ConnectorsContext";
import { useModalContext } from "../../context/ModalContext";

const ConnectorSection = () => {
  const { connectorsExplorerData, setConnectorsExplorerData } = useTreeContext();
  const { selectedConnector, setSelectedConnector } = useConnectorsContext();
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();
  const { getUserConfirmationForDelete } = useModalContext();

  const fetchAllConnectors = async () => {
    const endpoints = [
      { key: "email", path: "/email_connectors" }
      // Add other connectors here as needed
    ];

    try {
      setIsLoading(true);
      setError(null);

      const responses = await Promise.all(endpoints.map(({ path }) => axiosInstance.get(path)));

      const result = endpoints.reduce((acc, { key }, index) => {
        acc[key] = responses[index].data;
        return acc;
      }, {});

      const treeData = getTreeDataForConnectors(result);
      setConnectorsExplorerData(treeData);
    } catch (err) {
      console.error("Failed to fetch one or more connectors:", err);
      setError("Something went wrong while fetching connectors.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleNewInsert = async (connectorFolder, newConnectorName) => {
    try {
      const path = connectorFolder.id;
      const { data, message } = await axiosInstance.post(`/${path}`, { name: newConnectorName });

      const newFile = fileFolderConfig.getFileConfig(data.id, data.name, path, true, false);
      const newTree = insertNewFileOrFolder(connectorsExplorerData, connectorFolder, newFile);
      setConnectorsExplorerData(newTree);

      toastSuccess(message);
      navigateToConnector(data.id);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleDelete = async (connectorFile) => {
    try {
      const userConfirmed = await getUserConfirmationForDelete(connectorFile.name);
      if (!userConfirmed) return;

      const { message } = await axiosInstance.delete(`/${connectorFile.parentData}/${connectorFile.id}`);

      const newTree = deleteFolderOfFile(connectorsExplorerData, connectorFile);
      setConnectorsExplorerData(newTree);

      if (selectedConnector && selectedConnector.id && selectedConnector.id === connectorFile.id) {
        setSelectedConnector({
          id: ""
        });
        navigate("./");
      }

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const handleEdit = async (connectorFile, newName) => {
    try {
      const { message } = await axiosInstance.put(`/${connectorFile.parentData}/${connectorFile.id}`, {
        name: newName
      });

      const newTree = editFolderOrFile(connectorsExplorerData, connectorFile, newName);
      setConnectorsExplorerData(newTree);

      toastSuccess(message);
    } catch (error) {
      handleApiError(error);
    }
  };

  const navigateToConnector = (connectorFileId) => {
    navigate(`./${connectorFileId}`);
  };

  useEffect(() => {
    fetchAllConnectors();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <section className="main_section">
      <div className="sub_section">
        <ResizableComponent
          resizeSide="right"
          storageKey="left_sidebar_size"
          defaultSize={244}
          range={[244, 400]}
          collapsible={true}
        >
          <HeaderModes />
          <div className="d_flex">
            <Navbar />
            {error ? (
              <div className="w_100 d_flex flex_column align_center justify_center bg_gray">
                <p className="mb-2 text-red-600">Something went wrong</p>
                <Button onClick={fetchAllConnectors}>Retry</Button>
              </div>
            ) : (
              <LeftSideBar
                explorerData={connectorsExplorerData}
                setExplorerData={setConnectorsExplorerData}
                handleNewInsert={handleNewInsert}
                handleDelete={handleDelete}
                selectedScreen={selectedConnector}
                handleEdit={handleEdit}
                callbackFunction={navigateToConnector}
                type="Connectors"
                isLoader={isLoading}
              />
            )}
          </div>
        </ResizableComponent>
        {isLoading ? <Loader /> : !error && <Outlet />}
      </div>
    </section>
  );
};

export default ConnectorSection;
